import React, { useState } from "react";
import { Download, FileText, FileSpreadsheet, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import api from "@/lib/api";

const ACCENTS = {
  violet: { pick: "text-violet-800", pdf: "bg-violet-700 hover:bg-violet-800" },
  emerald: { pick: "text-emerald-800", pdf: "bg-emerald-700 hover:bg-emerald-800" },
};

function ReportPicker({ reports, value, onChange, testId }) {
  return (
    <div className="grid grid-cols-2 gap-1 rounded-full bg-slate-100 p-1" role="radiogroup" data-testid={`${testId}-report`}>
      {reports.map((r) => (
        <button key={r.key} type="button" role="radio" aria-checked={value === r.key} onClick={() => onChange(r.key)} data-testid={`${testId}-report-${r.key}`}
          className={`rounded-full px-3 py-1.5 text-sm font-semibold transition-colors ${value === r.key ? `bg-white shadow-sm ${r.pickClass}` : "text-slate-500 hover:text-slate-700"}`}>
          {r.label}
        </button>
      ))}
    </div>
  );
}

function Filters({ report, f, set, members, testId }) {
  return (
    <>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5"><Label>{report.dateLabel} from</Label><Input type="date" value={f.from} onChange={(e) => set("from", e.target.value)} data-testid={`${testId}-from`} /></div>
        <div className="space-y-1.5"><Label>{report.dateLabel} to</Label><Input type="date" value={f.to} onChange={(e) => set("to", e.target.value)} data-testid={`${testId}-to`} /></div>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Team member</Label>
          <Select value={f.member} onValueChange={(v) => set("member", v)}>
            <SelectTrigger data-testid={`${testId}-member`}><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value="all">Everyone</SelectItem>
              {members.map((m) => <SelectItem key={m.id} value={m.id} data-testid={`${testId}-member-${m.id}`}>{m.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Status</Label>
          <Select value={f.status} onValueChange={(v) => set("status", v)}>
            <SelectTrigger data-testid={`${testId}-status`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {report.statuses.map(([k, l]) => <SelectItem key={k} value={k} data-testid={`${testId}-status-${k}`}>{l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
    </>
  );
}

// Export button + window with two report options (same look as the Thespian Points export).
// reports: [{ key, label, endpoint, description, hint, dateLabel?, statuses? }] — no `statuses` = whole-team report, no filters.
export default function ReportExportDialog({ title, reports, members = [], accent = "violet", testId }) {
  const a = ACCENTS[accent];
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState(reports[0].key);
  const [f, setF] = useState({ from: "", to: "", member: "all", status: "all" });
  const [busy, setBusy] = useState(null);
  const report = reports.find((r) => r.key === key);
  const filtered = !!report.statuses;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const pick = (k) => { setKey(k); set("status", "all"); };

  const download = async (fmt) => {
    if (filtered && f.from && f.to && f.from > f.to) { toast.error("The start date must be before the end date"); return; }
    setBusy(fmt);
    try {
      const params = { fmt, ...(filtered && { ...(f.from && { from_date: f.from }), ...(f.to && { to_date: f.to }),
        ...(f.member !== "all" && { member_id: f.member }), status: f.status }) };
      const res = await api.get(report.endpoint, { params, responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || `${key}.${fmt}`;
      const el = document.createElement("a");
      el.href = URL.createObjectURL(res.data); el.download = name;
      document.body.appendChild(el); el.click(); el.remove();
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch { toast.error(`Unable to export ${report.label}`); }
    finally { setBusy(null); }
  };

  return (
    <>
      <Button variant="outline" className="rounded-full" onClick={() => setOpen(true)} data-testid={`${testId}-btn`}><Download className="mr-2 h-4 w-4" /> Export</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading">{title}</DialogTitle>
            <DialogDescription data-testid={`${testId}-description`}>{report.description}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <ReportPicker reports={reports.map((r) => ({ ...r, pickClass: a.pick }))} value={key} onChange={pick} testId={testId} />
            {filtered && <Filters report={report} f={f} set={set} members={members} testId={testId} />}
            <p className="text-xs text-slate-500">{report.hint}</p>
            <div className="grid grid-cols-2 gap-3">
              <Button className={`rounded-full ${a.pdf}`} disabled={!!busy} onClick={() => download("pdf")} data-testid={`${testId}-pdf`}>
                {busy === "pdf" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileText className="mr-2 h-4 w-4" />} PDF
              </Button>
              <Button variant="outline" className="rounded-full" disabled={!!busy} onClick={() => download("csv")} data-testid={`${testId}-csv`}>
                {busy === "csv" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-2 h-4 w-4" />} CSV
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
